import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Pagination,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import MaskedInput from 'react-text-mask';
import { FaFileExcel, FaSearch, FaEraser } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import { createDate, salvarEmExcel } from '~/Utils/Helpers';
import * as constants from '~/constant';

// Fonte: ERP/Reports/Imob/Graficos/ConcRecebimento.rdl + ModuloImob/UConcRecebimento.pas (launcher SSRS).
// Relatório read-only (GET apenas). Ordem: 'N'=por Usuário (default), 'S'=por Empreendimento (RN-05).
// Sentinel 0 = todos (RN-02): combos opcionais; "não selecionado" envia 0.
const ORDEM_USUARIO = 'N';
const ORDEM_EMPREENDIMENTO = 'S';

const ITENS_POR_PAGINA = 20;

// O lookup de usuários ativos (RN-09) traz ~728 itens. Pintar a lista inteira num
// dropdown plano trava a tela (mesma classe do W-0595/W-0949/W-0488). Por isso os
// dois combos de Usuário (Geração e Efetivação) usam AsyncSelect com filtro
// client-side por digitação e teto de resultados — paridade type-to-search.
const MIN_BUSCA = 2; // só monta o menu após digitar
const MAX_OPCOES = 50; // teto de itens pintados por busca (evita render de centenas)

const primeiroDiaMesAtual = () => {
  const now = new Date();
  return `01/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};

const hoje = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}/${String(
    now.getMonth() + 1
  ).padStart(2, '0')}/${now.getFullYear()}`;
};

const formatarValor = valor => {
  const numero = typeof valor === 'number' ? valor : Number(valor) || 0;
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatarData = valor => {
  if (!valor) return '';
  const d = createDate(valor);
  return d && d.isValid() ? d.format(constants.DATE_DEFAULT_FORMAT) : '';
};

export default function ConciliacaoRecebimentos() {
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioGeracao, setUsuarioGeracao] = useState(null);
  const [usuarioEfetivacao, setUsuarioEfetivacao] = useState(null);

  const [dataInicial, setDataInicial] = useState(primeiroDiaMesAtual());
  const [dataFinal, setDataFinal] = useState(hoje());
  const [somenteEfetivados, setSomenteEfetivados] = useState(false);
  const [ordem, setOrdem] = useState(ORDEM_USUARIO);

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [pagina, setPagina] = useState(1);

  // Combo de usuários ativos (Status=0) — RN-09. Mesma fonte para Geração e Efetivação.
  useEffect(() => {
    api
      .buscarConciliacaoRecebimentosUsuarios()
      .then(resp => {
        const lista = Array.isArray(resp) ? resp : [];
        setUsuarios(
          lista.map(u => ({
            value: u.codigo_usuario,
            label: u.nome_usuario,
          }))
        );
      })
      .catch(() => toast.error('Erro ao carregar usuários.'));
  }, []);

  // AsyncSelect dos usuários: filtra a lista já em memória (usuarios) por label e
  // limita o número de itens devolvidos. Só busca a partir de MIN_BUSCA chars, então
  // o menu nunca tenta pintar as centenas de usuários de uma vez (causa do freeze).
  // Roda síncrono via Promise.resolve.
  const carregarUsuarios = useCallback(
    entrada => {
      const termo = (entrada || '').trim().toLowerCase();
      if (termo.length < MIN_BUSCA) return Promise.resolve([]);
      const filtrados = usuarios
        .filter(o => o.label && o.label.toLowerCase().indexOf(termo) !== -1)
        .slice(0, MAX_OPCOES);
      return Promise.resolve(filtrados);
    },
    [usuarios]
  );

  const toServerDate = useCallback(str => {
    if (!str) return null;
    const d = createDate(str, constants.DATE_DEFAULT_FORMAT);
    return d.isValid() ? d.format(constants.DATE_FORMAT_SERVER) : null;
  }, []);

  const validar = useCallback(() => {
    // RN-03: Data Inicial + Data Final obrigatórias.
    if (!dataInicial || !dataFinal) {
      toast.error('Data Inicial e Data Final são obrigatórias');
      return false;
    }
    const di = createDate(dataInicial, constants.DATE_DEFAULT_FORMAT);
    const df = createDate(dataFinal, constants.DATE_DEFAULT_FORMAT);
    if (!di.isValid() || !df.isValid() || df.isBefore(di)) {
      toast.error('Período inválido');
      return false;
    }
    return true;
  }, [dataInicial, dataFinal]);

  const montarParams = useCallback(
    () => ({
      dataInicial: toServerDate(dataInicial),
      dataFinal: toServerDate(dataFinal),
      familia: ordem,
      efetivados: somenteEfetivados ? 'S' : 'N',
      // Sentinel 0 = todos (RN-02): combo vazio envia 0.
      usuarioGeracao: usuarioGeracao != null ? usuarioGeracao.value : 0,
      usuarioEfetivacao: usuarioEfetivacao != null ? usuarioEfetivacao.value : 0,
    }),
    [
      toServerDate,
      dataInicial,
      dataFinal,
      ordem,
      somenteEfetivados,
      usuarioGeracao,
      usuarioEfetivacao,
    ]
  );

  const gerar = async () => {
    if (!validar()) return;
    setLoading(true);
    setDados(null);
    setPagina(1);
    try {
      const resp = await api.buscarConciliacaoRecebimentos(montarParams());
      const lista = Array.isArray(resp) ? resp : [];
      setDados(lista);
      setPagina(1);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setLoading(false);
    }
  };

  const limpar = () => {
    setUsuarioGeracao(null);
    setUsuarioEfetivacao(null);
    setDataInicial(primeiroDiaMesAtual());
    setDataFinal(hoje());
    setSomenteEfetivados(false);
    setOrdem(ORDEM_USUARIO);
    setDados(null);
    setPagina(1);
  };

  const mostrarEmpreendimento = ordem === ORDEM_EMPREENDIMENTO;

  const exportarExcel = async () => {
    if (!dados || dados.length === 0) return;
    setExportando(true);
    try {
      const linhas = dados.map(item => {
        const linha = {
          'Usuário Geração': item.usuario,
          'Usuário Efetivação': item.usuario_efetivacao,
          Número: item.numero,
          Conta: item.apelido_conta,
          Parcela: item.parcela,
          Plano: item.plano,
          Vencimento: formatarData(item.data_vencimento),
          'Tipo Operação': item.tipo_operacao,
          'Tipo Título': item.tipo_titulo,
          Valor: formatarValor(item.valor),
          Data: formatarData(item.data),
        };
        if (mostrarEmpreendimento) {
          linha.Empreendimento = item.empreendimento;
        }
        return linha;
      });
      await salvarEmExcel(linhas, 'conciliacao-recebimentos');
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setExportando(false);
    }
  };

  // Total geral = soma de Valor (a conciliação é o agrupamento por usuário/empreendimento × valor).
  const totalGeral = useMemo(() => {
    if (!dados || dados.length === 0) return 0;
    return dados.reduce((acc, item) => {
      const v = typeof item.valor === 'number' ? item.valor : Number(item.valor) || 0;
      return acc + v;
    }, 0);
  }, [dados]);

  const possuiDados = dados && dados.length > 0;
  const colSpanVazio = mostrarEmpreendimento ? 12 : 11;

  // Paginação client-side. Os dados já vêm ordenados do servidor (RN-05),
  // então a fatia é tirada diretamente do array completo.
  const linhas = possuiDados ? dados : [];
  const totalRegistros = linhas.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / ITENS_POR_PAGINA));
  const inicio = (pagina - 1) * ITENS_POR_PAGINA;
  const rowsPaginadas = linhas.slice(inicio, inicio + ITENS_POR_PAGINA);
  const registroInicio = totalRegistros === 0 ? 0 : inicio + 1;
  const registroFim = Math.min(pagina * ITENS_POR_PAGINA, totalRegistros);

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;

    const maxVisiveis = 5;
    let paginaInicio = Math.max(1, pagina - Math.floor(maxVisiveis / 2));
    const paginaFim = Math.min(totalPaginas, paginaInicio + maxVisiveis - 1);
    if (paginaFim - paginaInicio + 1 < maxVisiveis) {
      paginaInicio = Math.max(1, paginaFim - maxVisiveis + 1);
    }

    const pagList = [];
    for (let i = paginaInicio; i <= paginaFim; i++) {
      pagList.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap px-3 py-2 border-top">
        <small className="text-muted">
          Mostrando {registroInicio} até {registroFim} de {totalRegistros}{' '}
          {totalRegistros === 1 ? 'registro' : 'registros'}
        </small>
        <Pagination className="mb-0" size="sm">
          <Pagination.First onClick={() => setPagina(1)} disabled={pagina <= 1} />
          <Pagination.Prev onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1} />
          {paginaInicio > 1 && <Pagination.Ellipsis disabled />}
          {pagList.map(p => (
            <Pagination.Item key={p} active={p === pagina} onClick={() => setPagina(p)}>
              {p}
            </Pagination.Item>
          ))}
          {paginaFim < totalPaginas && <Pagination.Ellipsis disabled />}
          <Pagination.Next
            onClick={() => setPagina(pagina + 1)}
            disabled={pagina >= totalPaginas}
          />
          <Pagination.Last
            onClick={() => setPagina(totalPaginas)}
            disabled={pagina >= totalPaginas}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="mt-3">
      <Header title="Conciliação dos Recebimentos" />

      <Card className="mt-3 mb-3">
        <Card.Body>
          <Row className="mb-2">
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Data Inicial</Form.Label>
              <MaskedInput
                mask={constants.MASK_DATE}
                className="form-control"
                style={{ fontSize: '0.875rem', height: '31px', padding: '2px 8px' }}
                placeholder="dd/mm/aaaa"
                value={dataInicial}
                onChange={e => setDataInicial(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Data Final</Form.Label>
              <MaskedInput
                mask={constants.MASK_DATE}
                className="form-control"
                style={{ fontSize: '0.875rem', height: '31px', padding: '2px 8px' }}
                placeholder="dd/mm/aaaa"
                value={dataFinal}
                onChange={e => setDataFinal(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Usuário Geração</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                loadOptions={carregarUsuarios}
                isClearable
                placeholder="Digite para buscar..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                value={usuarioGeracao}
                onChange={opt => setUsuarioGeracao(opt)}
                loadingMessage={() => 'Buscando...'}
                noOptionsMessage={({ inputValue }) =>
                  inputValue && inputValue.trim().length >= MIN_BUSCA
                    ? 'Sem opções'
                    : 'Digite ao menos 2 caracteres'
                }
              />
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Usuário Efetivação</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                loadOptions={carregarUsuarios}
                isClearable
                placeholder="Digite para buscar..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                value={usuarioEfetivacao}
                onChange={opt => setUsuarioEfetivacao(opt)}
                loadingMessage={() => 'Buscando...'}
                noOptionsMessage={({ inputValue }) =>
                  inputValue && inputValue.trim().length >= MIN_BUSCA
                    ? 'Sem opções'
                    : 'Digite ao menos 2 caracteres'
                }
              />
            </Col>
          </Row>

          <Row className="align-items-end">
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1 d-block">Ordenar por</Form.Label>
              <Form.Check
                inline
                type="radio"
                id="radio-ordem-usuario"
                name="ordem"
                label="Usuário"
                checked={ordem === ORDEM_USUARIO}
                onChange={() => setOrdem(ORDEM_USUARIO)}
              />
              <Form.Check
                inline
                type="radio"
                id="radio-ordem-empreendimento"
                name="ordem"
                label="Empreendimento"
                checked={ordem === ORDEM_EMPREENDIMENTO}
                onChange={() => setOrdem(ORDEM_EMPREENDIMENTO)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                type="checkbox"
                id="check-somente-efetivados"
                label="Somente Efetivados"
                checked={somenteEfetivados}
                onChange={e => setSomenteEfetivados(e.target.checked)}
              />
            </Col>
            <Col md={5} className="text-right">
              <Button variant="secondary" className="mr-2" onClick={limpar} disabled={loading}>
                <FaEraser className="mr-1" /> Limpar
              </Button>
              <Button variant="primary" onClick={gerar} disabled={loading}>
                <FaSearch className="mr-1" /> Gerar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="success"
          size="sm"
          onClick={exportarExcel}
          disabled={!possuiDados || exportando}
        >
          {exportando ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : (
            <FaFileExcel className="mr-1" />
          )}
          Exportar Excel
        </Button>
      </div>

      <Card>
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          Resultado {possuiDados ? `(${dados.length} registros)` : ''}
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
            <Table striped bordered hover size="sm" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Usuário Geração</th>
                  <th>Usuário Efetivação</th>
                  <th>Número</th>
                  <th>Conta</th>
                  <th className="text-right">Parcela</th>
                  <th className="text-right">Plano</th>
                  <th>Vencimento</th>
                  <th>Tipo Operação</th>
                  <th>Tipo Título</th>
                  <th className="text-right">Valor</th>
                  <th>Data</th>
                  {mostrarEmpreendimento && <th>Empreendimento</th>}
                </tr>
              </thead>
              <tbody>
                {possuiDados ? (
                  rowsPaginadas.map((item, idx) => (
                    <tr
                      key={`${item.numero}-${item.parcela}-${item.plano}-${item.tipo_titulo}-${idx}`}
                    >
                      <td>{item.usuario}</td>
                      <td>{item.usuario_efetivacao}</td>
                      <td>{item.numero}</td>
                      <td>{item.apelido_conta}</td>
                      <td className="text-right">{item.parcela}</td>
                      <td className="text-right">{item.plano}</td>
                      <td>{formatarData(item.data_vencimento)}</td>
                      <td>{item.tipo_operacao}</td>
                      <td>{item.tipo_titulo}</td>
                      <td className="text-right">{formatarValor(item.valor)}</td>
                      <td>{formatarData(item.data)}</td>
                      {mostrarEmpreendimento && <td>{item.empreendimento}</td>}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpanVazio} className="text-center text-muted p-3">
                      Não existem dados para o período
                    </td>
                  </tr>
                )}
              </tbody>
              {possuiDados && (
                <tfoot>
                  <tr className="font-weight-bold">
                    <td colSpan={9} className="text-right">
                      Total Geral
                    </td>
                    <td className="text-right">{formatarValor(totalGeral)}</td>
                    <td colSpan={mostrarEmpreendimento ? 2 : 1} />
                  </tr>
                </tfoot>
              )}
            </Table>
            {renderizarPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
